import React, { useCallback, useEffect, useRef } from 'react';
import campusDiveHero from '../../assets/images/campusDiveHero.gif';
import nbnbHeroLanding from '../../assets/images/nbnbHeroLanding.gif';
import landingVaDisabilityClaim from '../../assets/images/landingVaDisabilityClaim.png';
import landingMhaNav from '../../assets/images/landingMhaNav.png';
import ProjectShowcaseCard from '../cards/homeCards/projectShowcaseCard';
import showcaseCardStyles from '../cards/homeCards/projectShowcaseCard.module.scss';
import styles from './featuredProjectShowcase.module.scss';

const DESKTOP_SCROLL_MQ = '(min-width: 1100px)';
const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';
const FLIP_MAX_DEG = 88;

const SHOWCASE_PROJECTS = [
  {
    key: 'nbnb',
    tags: [
      'Product Design',
      'UX Design',
      'UI Design',
      'Branding',
      'Mobile App',
      'Market research',
      'Branding',
      'Zero to Launch',
      'Social Media',
      'Startup',
    ],
    title: 'Connecting a Diverse Golfing Community Through Strategic Product Design',
    subhead: 'Mobile App & Product Design',
    imageSrc: nbnbHeroLanding,
    imageAlt: 'NBNB mobile and laptop mockups for the golf community product',
    to: '/NBNB',
    imgClassName: showcaseCardStyles.imageFeaturedContain,
    mediaClassName: `${showcaseCardStyles.mediaWide} ${showcaseCardStyles.mediaNbnbHero}`,
    tagRowBreakBeforeIndex: 5,
  },
  {
    key: 'disability-claims',
    tags: [
      'User Research',
      'UX Design',
      'Civic Tech',
      'Enterprise',
      'A/B Testing',
      'Accessibility',
      'Design System',
      'Mobile-first web design',
    ],
    title:
      'Designing for Dignity: Improving Accessibility & Data Integrity in a High-Stakes Disability Claim Flow',
    subhead: 'A11y Usability Research & Iterative Design',
    imageSrc: landingVaDisabilityClaim,
    imageAlt: 'Laptop mockup of a VA disability compensation claim flow in a web browser',
    to: '/disability-claims',
    ctaLabel: 'View Case Study',
  },
  {
    key: 'campus-dive',
    tags: [
      'Product Design',
      'UX Design',
      'UI Design',
      'Data Visualization',
      'Edtech',
      'Heuristic Evaluation',
      'Branding',
      'Feature Development',
      'B2C',
      'Startup',
    ],
    title: 'Closing the College Guidance Gap Through Better Product Design',
    subhead: 'Product design for scalability',
    imageSrc: campusDiveHero,
    imageAlt: 'Campus Dive laptop and phone mockups showing academic profile and sign-in experience',
    to: '/CampusDive',
    imgClassName: showcaseCardStyles.imageFeaturedContain,
    mediaClassName: showcaseCardStyles.mediaWide,
  },
  {
    key: 'mha',
    tags: [
      'Product Design',
      'UI Design',
      'User Research',
      'UX Design',
      'Click Testing',
      'Information Architecture',
      'Navigation',
      'Internal Tools',
      'Health Tech',
    ],
    title: 'Re-Architecting Navigation to Critical Healthcare Systems',
    subhead: 'UI Design & Information Architecture',
    imageSrc: landingMhaNav,
    imageAlt: 'Desktop mockup of a healthcare member portal with navigation and dashboard',
    to: '/MHA',
    mediaClassName: showcaseCardStyles.mediaWide,
    tagRowBreakBeforeIndex: 3,
  },
];

function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

function updateFlipPanel(panel, index, total) {
  const face = panel.querySelector('[data-flip-face]');
  if (!face) return;

  const rect = panel.getBoundingClientRect();
  const vh = window.innerHeight;
  const ratio = rect.top / vh;
  let rotateY = Math.max(-FLIP_MAX_DEG, Math.min(FLIP_MAX_DEG, ratio * FLIP_MAX_DEG));

  if (index === 0) {
    rotateY = Math.min(0, rotateY);
  }
  if (index === total - 1) {
    rotateY = Math.max(0, rotateY);
  }

  const rad = (rotateY * Math.PI) / 180;
  const translateZ = -Math.abs(Math.sin(rad)) * 160;
  const opacity = Math.max(0.12, Math.abs(Math.cos(rad)));

  face.style.setProperty('--flip-rotate-y', `${rotateY}deg`);
  face.style.setProperty('--flip-translate-z', `${translateZ}px`);
  face.style.setProperty('--flip-opacity', String(opacity));
  panel.style.zIndex = String(Math.round(opacity * 100));
}

function resetFlipPanel(panel) {
  const face = panel.querySelector('[data-flip-face]');
  if (!face) return;
  face.style.removeProperty('--flip-rotate-y');
  face.style.removeProperty('--flip-translate-z');
  face.style.removeProperty('--flip-opacity');
  panel.style.removeProperty('z-index');
}

function useScrollFlip(panelRefs, enabled) {
  const firstPassDoneRef = useRef(false);
  const hasScrolledDownRef = useRef(false);

  const tick = useCallback(() => {
    const panels = panelRefs.current.filter(Boolean);
    const total = panels.length;

    if (!total || firstPassDoneRef.current) {
      panels.forEach(resetFlipPanel);
      return;
    }

    if (window.scrollY > 8) {
      hasScrolledDownRef.current = true;
    }

    if (!hasScrolledDownRef.current) {
      panels.forEach(resetFlipPanel);
      return;
    }

    panels.forEach((panel, index) => {
      updateFlipPanel(panel, index, total);
    });

    const lastPanel = panels[total - 1];
    if (lastPanel && lastPanel.getBoundingClientRect().top <= -window.innerHeight * 0.2) {
      firstPassDoneRef.current = true;
      panels.forEach(resetFlipPanel);
    }
  }, [panelRefs]);

  useEffect(() => {
    if (!enabled) {
      panelRefs.current.forEach((panel) => {
        if (panel) resetFlipPanel(panel);
      });
      return undefined;
    }

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        tick();
      });
    };

    tick();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    const panels = panelRefs.current;

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      panels.forEach((panel) => {
        if (panel) resetFlipPanel(panel);
      });
    };
  }, [enabled, tick, panelRefs]);
}

export default function FeaturedProjectShowcase({ sectionAriaLabel = 'Featured products' }) {
  const isDesktop = useMediaQuery(DESKTOP_SCROLL_MQ);
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_MQ);
  const flipPanelRefs = useRef([]);
  const flipEnabled = isDesktop && !prefersReducedMotion;

  useScrollFlip(flipPanelRefs, flipEnabled);

  return (
    <section className={styles.featuredSection} aria-label={sectionAriaLabel}>
      <div className={styles.featuredScrollport}>
        {SHOWCASE_PROJECTS.map((project, index) => (
          <div
            key={project.key}
            className={styles.flipPanel}
            ref={(el) => {
              flipPanelRefs.current[index] = el;
            }}
          >
            <div className={styles.flipPanelSticky}>
              <div className={styles.flipCubeFace} data-flip-face>
                <ProjectShowcaseCard {...project} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
